import { useCallback, useEffect, useState, useContext } from "react";
import { FileState, GetAssetSizeResponse } from "../../proto/api_pb";
import { GetTrackFilesResponse } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { Box, Typography, LinearProgress } from "@mui/material";
import { formatFileSize, getAssetSize } from "../../services/trackService";
import { deleteTrackFile, fetchTrackFiles } from "../../services/trackService";
import { TrackFiles } from "./TrackFile";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { FileObj } from "./types";
import { TrackDeleteConfirmModal } from "../Modal/TrackDeleteConfirmModal";
import { toast } from "react-toastify";
import { AuthContext } from "../../contexts";
import { styles } from "./styles";
import { FONT_FAMILY } from "../../theme";

export const TrackFileGroup: React.FC<{
  id: string | undefined;
  isFileAdded?: boolean;
}> = ({ id, isFileAdded }) => {
  const [trackFiles, setTrackFiles] = useState<FileObj[]>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [isOpen, setIsOpen] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState("");
  const { userId } = useContext(AuthContext);
  const [pageLoaded, setPageLoaded] = useState(false);

  const handleFetchTrackFiles = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = (await fetchTrackFiles(
        token,
        id!,
        userId
      )) as GetTrackFilesResponse;
      const tempTrackFilesList = response.getTrackFilesList();
      const filesObjList: FileObj[] = [];
      for (const file of tempTrackFilesList) {
        if (file.getFileState() === FileState.FILE_STATE_ACTIVE) {
          try {
            const fileSize = (await getAssetSize(
              token,
              file.getFileUrl()
            )) as GetAssetSizeResponse;

            filesObjList.push({
              trackFile: file,
              fileSize: fileSize.getSizeGb() ? fileSize.getSizeGb() : 0,
            });
          } catch (error) {
            console.log(error);
          }
        }
      }
      setTrackFiles([...filesObjList]);
      setPageLoaded(true);
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
    // eslint-disable-next-line
  }, [getValidAccessToken, id, isFileAdded]);

  useEffect(() => {
    handleFetchTrackFiles();
  }, [handleFetchTrackFiles]);

  const handleDeleteFile = async () => {
    const updatedFiles = trackFiles.filter(
      (file) => file.trackFile.getId() !== fileToDeleteId
    );
    setTrackFiles([...updatedFiles]);
    const deleteTrackFileResponse = await deleteTrackFile(
      fileToDeleteId,
      await getValidAccessToken()
    );
    if (deleteTrackFileResponse instanceof RpcError) {
      throw deleteTrackFileResponse;
    }
    handleCloseDeleteModal();
    toast.success("File removed successfully");
  };

  const handleOpenDeleteModal = (id: string) => {
    setIsOpen(true);
    setFileToDeleteId(id);
  };
  const handleCloseDeleteModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <TrackDeleteConfirmModal
        deleteConfirmModalOpen={isOpen}
        closeDeleteConfirmModal={handleCloseDeleteModal}
        handleDeleteTrack={handleDeleteFile}
        type="File"
      />
      <Box>
        {pageLoaded && trackFiles.length > 0 && (
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 18,
              fontWeight: "bold",
              borderBottom: "0.5px solid #9FA4A9",
              marginBottom: "5px",
            }}
            variant="h6"
          ></Typography>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {trackFiles &&
            trackFiles.map((el: FileObj, i) => {
              const url = el.trackFile.getFileUrl();
              const id = el.trackFile.getId();
              const label = el.trackFile.getLabel();
              const fileName = el.trackFile.getFilename();
              const type = el.trackFile.getType();
              const timeStamp = el.trackFile.getTimestamp();
              const ownerId = el.trackFile.getOwnerId();
              const fileSize = el.fileSize;
              return (
                <>
                  <TrackFiles
                    fileId={id}
                    key={i}
                    url={url}
                    label={label}
                    timeStamp={timeStamp}
                    ownerId={ownerId}
                    fileName={fileName}
                    fileType={type}
                    fileSize={fileSize ? formatFileSize(fileSize) : "-"}
                    handleOpenDeleteModal={handleOpenDeleteModal}
                    collaborators={el.trackFile.getCollaboratorsList()}
                    file={el.trackFile}
                  />
                </>
              );
            })}
        </Box>
        {!pageLoaded && (
          <Box sx={styles.linearProgressMainContainer}>
            <Box sx={styles.linearProgressContainer}>
              <LinearProgress color="inherit" />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
